import React, { useState } from 'react';
import filter from 'lodash/filter';
import get from 'lodash/get';

import { Button, ButtonTypes } from 'src/presentation/components/agent_desktop_components';
import communicationQueuesResource from 'src/actions/communicationQueues/communicationQueuesResource';
import companyPhoneNumbersResource from 'src/actions/companyPhoneNumber/companyPhoneNumbersResource';
import { formatPhone } from 'src/actions/shared/phonesHelper';
import { InternalLink } from 'src/presentation/components/pages/shared/Links';
import Ivr from 'src/models/Ivrs/Ivr';
import routingGroupsResource from 'src/actions/routingGroups/routingGroupsResource';
import SetupPhoneModal from 'src/presentation/components/pages/channels/shared/setupPhoneModal/SetupPhoneModal';
import { Spinner } from 'src/presentation/components/pages/shared/Spinner';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import useResource, { isLoadingStatus } from 'src/presentation/hooks/useResource';
import useVoiceConfiguration from 'src/presentation/components/pages/channels/voice/hooks/useVoiceConfiguration';
import voiceConfigurationResource from 'src/actions/channels/voiceConfigurationResource';
import VoiceDescription from './VoiceDescription';
import VoiceEditor from './VoiceEditor';
import VoiceEmptyPage from './VoiceEmptyPage';

const TableActions = ({ handleSetUpVoice }): JSX.Element => (
  <Button data-aid="action-button-manage-voice" onClick={handleSetUpVoice} buttonType={ButtonTypes.PRIMARY}>
    Set up voice
  </Button>
);

const linkToIvr = (attr: string, item: Ivr) => {
  const ivrId = get(item, `${attr}Id`, '');
  const ivrName = get(item, `${attr}Name`);
  return ivrName ? (
    <InternalLink target="_blank" to={`/ivrs/${ivrId}`}>
      {ivrName}
    </InternalLink>
  ) : null;
};

const columnConfigWithInternalName = [
  {
    attr: 'number',
    customColumnStyle: () => ({ width: '240px' }),
    customRenderer: item => formatPhone(item.number),
    label: 'Phone number',
    sorting: true,
  },
  { attr: 'name', label: 'Internal name' },
  {
    attr: 'initialIvrName',
    customRenderer: item => linkToIvr('initialIvr', item),
    label: 'Initial IVR',
    sorting: ({ initialIvrName }) => initialIvrName,
  },
  {
    attr: 'queueIvrName',
    customRenderer: item => linkToIvr('queueIvr', item),
    label: 'Queue IVR',
    sorting: ({ queueIvrName }) => queueIvrName,
  },
];

const VoiceConfigurations: TablePageRender = ({ root }) => {
  // preload needed resources
  useResource(companyPhoneNumbersResource);
  useResource(communicationQueuesResource);
  useResource(routingGroupsResource);

  const { errors, loadingState, voiceEndpoints } = useVoiceConfiguration();

  const { resetErrors } = voiceConfigurationResource.actions;
  const [showModal, openModal] = useState(false);
  const handleCloseModal = () => openModal(false);

  const sortingConfig = { attr: 'number' };

  function filterFunc(items, searchString) {
    if (!searchString) {
      return items;
    }
    const results = filter(items, item => {
      const search = searchString.toLowerCase();
      return (
        item.number.includes(search) ||
        get(item, 'initialIvrName', '').toLowerCase().includes(search) ||
        get(item, 'queueIvrName', '').toLowerCase().includes(search)
      );
    });
    return results;
  }

  const handleSetUpVoice = () => openModal(true);

  const setUpVoiceButton = <TableActions handleSetUpVoice={handleSetUpVoice} />;

  return (
    <>
      {isLoadingStatus(loadingState) && voiceEndpoints.length === 0 ? (
        <Spinner />
      ) : voiceEndpoints.length === 0 ? (
        <VoiceEmptyPage handleSetUpVoice={handleSetUpVoice} />
      ) : (
        <TablePage
          actions={setUpVoiceButton}
          columnConfig={columnConfigWithInternalName}
          data-aid="admin-channels-voice-page"
          description={<VoiceDescription />}
          editor={VoiceEditor}
          errors={errors}
          filterFunc={filterFunc}
          filterPlaceholder="Search by digits or IVR"
          initialSorting={sortingConfig}
          isLoading={isLoadingStatus(loadingState)}
          items={voiceEndpoints}
          name="Voice"
          resetErrors={resetErrors}
          root={root}
        />
      )}
      {showModal && <SetupPhoneModal handleCloseModal={handleCloseModal} />}
    </>
  );
};

export default VoiceConfigurations;
